import React, { useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet';
import { LocalizeContextProps, Translate, withLocalize } from "react-localize-redux";
import { Grid, IconButton, Snackbar, Typography, useTheme, Tabs, Tab, Box } from '@mui/material';
import Modal from '../../../components/general/modal';
import { usePatients, useSnackBarState } from '../../../hooks';
import { useBills } from '../../../hooks/useBills';
import { Alert } from '@mui/material';
import { ButtonAdd, TypographyStyled } from '../../../components/general/mini_components';
import { IPatient, ISurvey, ReduxStore, SnackbarType } from '../../../constants/types';
import { isBillCancelled, isBillEditable } from './billDocumentAccess';
import { Bill, BillingInfo, BillItemModes, DocumentStatus, DocumentType, CreatePaymentRequest } from './types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { createBillService, getBillsPatientService, getDocumentsService, updateBillItemsStatusService, updateBillService, updateDocumentType, getBillByUuidService, cancelBillService } from '../../../services/billing';
import Loader from '../../../components/Loader';
import { hasDiscountsActive, stringDatePostgresToDate } from '../../../utils/index.jsx';
import { generateBillPDFFilename } from '../../../utils/bill';
import EditBilling from './Edit';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import BillsPatient from './BillsPatient';
import BillsTable from './BillsTable';
import { FindPatient } from './find_patient';

import { HOSPITAL_BILLING, HOSPITAL_BILLING_CREATE_BILL, HOSPITAL_BILLING_EDIT, HOSPITAL_BILLING_VIEW_DOCUMENT, HOSPITAL_BILLING_INSURANCE, HOSPITAL_BILLING_INSURANCE_VIEW, HOSPITAL_BILLING_ACCOUNTABILITY } from '../../../routes/urls';
import { getBillableComboAction, resetBillItems } from '../../../redux/actions/billingActions';
import { TYPE_ADDITIONAL_INFO_SURVEY } from '../../../constants';
import SectionHeader from '../../components/SectionHeader';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import BarChartIcon from '@mui/icons-material/BarChart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DescriptionIcon from '@mui/icons-material/Description';
import BillView from './BillView';
import BillCreate from './BillCreate';
import InsuranceBillsTable from './InsuranceBillsTable';
import InsuranceBillView from './InsuranceBillView';
import AccountabilityTable from './AccountabilityTable';
import { fetchPatient } from '../../../db/index';
import { green, red, blue, grey, orange } from '@mui/material/colors';
import { Done, Edit, Lock, MoneyOff } from '@mui/icons-material';
import { BillStatus } from '../Request/types';
import { useBillPaymentData } from '../../../hooks/useBillPaymentData';
import { useOrganizations } from '../../../hooks/useOrganizations';
import { PERMISSION } from '../../../components/investigation/share/user_roles';
import { InsuranceClaimStatus } from '../InsuranceClaims/types';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
// TODO: Insurance report functionality may be removed entirely
// Removed imports: InsuranceBillsReportModal, prepareInsuranceBillsReport, InsuranceBillsReportPDF, InsuranceClaimsApiService
import { downloadPDF } from '../../../components/pdf/utils/pdfDownload';
import { usePDFConfig } from '../../../hooks/usePDFConfig';
import BillPDFDocument from '../../../components/pdf/documents/BillPDFDocument';
import { useDepartments, usePatientInsurance } from '../../../hooks';
import { Button } from '@mui/material';
import { 
    Edit as EditIcon,
    Send as SendIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Warning as WarningIcon,
    Paid as PaidIcon
} from '@mui/icons-material';

interface PropsRedux {
    investigations: any,
    patients: { data: any },
    uuidInvestigation: string,
}

export enum TypeBillItemUpdate{
    BillItems = "billItems",
    BillItemsStatus = "billItemsStatus"
}

export function documentTypeToIcon(type:DocumentType){
    switch(type){
        case DocumentType.BUDGET:
            return <BarChartIcon />;
        case DocumentType.SUMMARY:
            return <DescriptionIcon />;
        case DocumentType.INVOICE:
            return <ReceiptIcon />;
    }
}

export function documentStatusToIcon(type:DocumentStatus){
    switch(type){
        case DocumentStatus.DRAFT:
            return <Edit />;
        case DocumentStatus.CLOSED:
            return <Lock />;
        // SHER-426: CANCELLED (added by SHER-423) had no icon, so renderStatus fed
        // undefined to ColourButton's React.cloneElement → React #267 crashed the
        // whole billing table when a cancelled document was in the visible page.
        case DocumentStatus.CANCELLED:
            return <CancelIcon />;
    }
}

export function paidStatusToIcon(type:BillStatus){
    switch(type){
        case BillStatus.PAID:
            return <Done />;
        case BillStatus.PENDING_PAYMENT:
            return <MoneyOff/>;
    }
}

export function paidStatusToColor(type:BillStatus){
    switch(type){
        case BillStatus.PAID:
            return green[500];
        case BillStatus.PENDING_PAYMENT:
            return red[500];
    }
}

// Insurance Claim Status utilities
import { INSURANCE_CLAIM_STATUS_OPTIONS } from '../InsuranceClaims/fields';

// Map status values to icons
const STATUS_TO_ICON_MAP: Record<InsuranceClaimStatus, JSX.Element> = {
    [InsuranceClaimStatus.DRAFT]: <EditIcon />,
    [InsuranceClaimStatus.SUBMITTED]: <SendIcon />,
    [InsuranceClaimStatus.APPROVED]: <CheckCircleIcon />,
    [InsuranceClaimStatus.DENIED]: <CancelIcon />,
    [InsuranceClaimStatus.PARTIALLY_APPROVED]: <WarningIcon />,
    [InsuranceClaimStatus.PAID]: <PaidIcon />
};

// Map status values to colors
const STATUS_TO_COLOR_MAP: Record<InsuranceClaimStatus, string> = {
    [InsuranceClaimStatus.DRAFT]: '#9e9e9e', // grey[500]
    [InsuranceClaimStatus.SUBMITTED]: '#2196f3', // blue[500]
    [InsuranceClaimStatus.APPROVED]: '#4caf50', // green[500]
    [InsuranceClaimStatus.DENIED]: '#f44336', // red[500]
    [InsuranceClaimStatus.PARTIALLY_APPROVED]: '#ff9800', // orange[500]
    [InsuranceClaimStatus.PAID]: '#388e3c' // green[700]
};

export function insuranceClaimStatusToIcon(status: InsuranceClaimStatus) {
    return STATUS_TO_ICON_MAP[status] || <EditIcon />;
}

export function insuranceClaimStatusToColor(status: InsuranceClaimStatus) {
    return STATUS_TO_COLOR_MAP[status] || '#bdbdbd'; // grey[400]
}

// Helper function to get all available status options
export function getInsuranceClaimStatusOptions() {
    return INSURANCE_CLAIM_STATUS_OPTIONS;
}

const BillingRedux: React.FC<PropsRedux> = ({ investigations, patients }) => {
    
    const investigation = investigations.data && investigations.currentInvestigation ? investigations.currentInvestigation : null;
    const [bills, setBills] = useState<Bill[]>([]);
    const [loading, setLoading] = useState(false);
    const [showSnackbar, setShowSnackbar] = useSnackBarState();
    const location = useLocation();
    const history = useHistory();
    let uuidPatient = useParams<{uuidPatient?:string}>().uuidPatient;
    let uuidDocument = useParams<{uuidDocument?:string}>().uuidDocument;
    let uuidBill = useParams<{uuidBill?:string}>().uuidBill;

    // Determine action based on route
    let action: BillActions;
    if (location.pathname === HOSPITAL_BILLING_CREATE_BILL) {
        action = BillActions.CREATE;
    } else if (uuidBill && location.pathname.includes('/billing/insurance/')) {
        action = BillActions.VIEW_INSURANCE_BILL;
    } else if (location.pathname === HOSPITAL_BILLING_ACCOUNTABILITY) {
        action = BillActions.ACCOUNTABILITY;
    } else if (location.pathname === HOSPITAL_BILLING_INSURANCE || location.pathname.startsWith('/billing/insurance')) {
        action = BillActions.INSURANCE_BILLS;
    } else if (uuidPatient) {
        action = BillActions.PATIENT_BILLS;
    } else if (uuidDocument) {
        action = BillActions.VIEW;
    } else {
        action = BillActions.DEFAULT;
    }
    const canCreateInsuranceClaims = investigation && investigation.permissions && investigation.permissions.includes("CREATE_INSURANCE_CLAIMS");
    const hasDiscounts = investigation && investigation.billingInfo && investigation.billingInfo.params && hasDiscountsActive(investigation.billingInfo.params, investigation.permissions);
    const canCreateBills = investigation && investigation.permissions && investigation.permissions.includes("CREATE_BILLING");
    const canCancelDocument = Boolean(investigation && investigation.permissions && investigation.permissions.includes(PERMISSION.CANCEL_BILLING_DOCUMENT));
    const canUpdateInsurance = investigation && investigation.permissions && investigation.permissions.includes(PERMISSION.UPDATE_BILL_PRICES_ON_INSURANCE_CHANGE);
    const canEditPharmacyPrices = investigation && investigation.permissions && investigation.permissions.includes(PERMISSION.MANAGE_PHARMACY_CENTRAL);
    const dispatch = useDispatch();

    // SHER-474: the DEFAULT browse (all patients, no uuidPatient) is server-paginated.
    // Every OTHER action keeps the existing bulk `bills` state (bill create/update/split/
    // VIEW depend on it), so only this path switches to useBills.
    const isDefaultBrowse = action === BillActions.DEFAULT && !uuidPatient;
    const [browsePage, setBrowsePage] = useState<number>(0);
    const [browseRowsPerPage, setBrowseRowsPerPage] = useState<number>(10);
    const [browseFilters, setBrowseFilters] = useState<{ statuses?: number[]; types?: number[]; insuranceCodes?: string[]; dateFrom?: string; dateTo?: string }>({});
    const browseServer = useBills({
        investigationUuid: investigation?.uuid ?? '',
        page: browsePage,
        rowsPerPage: browseRowsPerPage,
        filters: browseFilters,
        enabled: Boolean(investigation) && isDefaultBrowse,
    });
    function onBrowsePageChange(page: number, rowsPerPage: number) {
        setBrowsePage(page);
        setBrowseRowsPerPage(rowsPerPage);
    }
    // Map BillsTable's raw chip state to the API filters. Only the DB-backed chips are
    // server-side; the derived paid/claim-status chips are hidden in server mode (deferred).
    function onBrowseFilterChange(f: { documentStatusFilter?: number[]; typeFilter?: number[]; selectedInsurance?: string; startDate?: string; endDate?: string }) {
        setBrowsePage(0);
        setBrowseFilters({
            statuses: f.documentStatusFilter && f.documentStatusFilter.length ? f.documentStatusFilter : undefined,
            types: f.typeFilter && f.typeFilter.length ? f.typeFilter : undefined,
            insuranceCodes: f.selectedInsurance ? [f.selectedInsurance] : undefined,
            dateFrom: f.startDate || undefined,
            dateTo: f.endDate || undefined,
        });
    }

    function navigateToHomeBilling(){
        history.push(HOSPITAL_BILLING);
    }

    function handleError(error:any){
        if(error.status === 401){
            setShowSnackbar({show:true, message: "hospital.billing.bill.error.permission", severity: 'error'});
        }
        else{
            switch(error.errorCode){
                case "PAYMENT_EXCEEDS_REMAINING_BALANCE":
                    setShowSnackbar({show:true, message: "hospital.billing.bill.error.payment_exceeds_remaining_balance", severity: 'error'});
                    break;
                //We are migrating towards messages in text.
            case 1:
                setShowSnackbar({show:true, message: "hospital.billing.bill.error.changed", severity: 'error'});
                break;
            case 2:
                setShowSnackbar({show:true, message: "hospital.billing.bill.error.discounts_hidden", severity: 'error'});
                break;
            default:
                setShowSnackbar({show:true, message: "general.error", severity: 'error'});
                break;
            }
        }
    }

    async function onCreateOrUpdateBill(bill: Bill, typeUpdate:TypeBillItemUpdate = TypeBillItemUpdate.BillItems, paymentRecord?: CreatePaymentRequest, onBillUpdated?: (bill: Bill) => void) {
        console.log("onCreateBill", bill);
        try{
            setLoading(true);
            let response: { status: number, bill?: Bill };
            if (bill.uuid) {
                if(typeUpdate === TypeBillItemUpdate.BillItems){
                    response = await updateBillService(investigation.uuid, bill.uuid, bill);
                }
                else{
                    // Handle new response structure for bill items status update
                    const billUpdateResponse = await updateBillItemsStatusService(investigation.uuid, bill.uuid, bill, paymentRecord);
                    response = { status: billUpdateResponse.status, bill: billUpdateResponse.bill };
                }
            }
            else {
                response = await createBillService(investigation.uuid, bill.uuidPatient, bill);
            }

            if (response.status === 200 && response.bill) {
                if (response.bill.uuid) {
                    setShowSnackbar({ message: "hospital.billing.bill.success.updated", show: true, severity: "success" });
                }
                else {
                    setShowSnackbar({ message: "hospital.billing.bill.success.created", show: true, severity: "success" });
                }
            }
            else {
                
                setShowSnackbar({show:true, message: "hospital.billing.bill.error.permission", severity: 'error'});
            }
              
            if(typeof response.bill !== "undefined"){
                const tempBills = [...bills];
                
                const uuidBill = response.bill.uuid;
                const existingBillIndex = tempBills.findIndex((aBill) => uuidBill === aBill.uuid);
                if (existingBillIndex !== -1) {
                    tempBills[existingBillIndex] = response.bill;
                }
                else {
                    tempBills.push(response.bill);
                    tempBills.sort((billA, billB) => {
                        if(billA.updatedAt && billB.updatedAt){
                            return stringDatePostgresToDate(billA.updatedAt).getTime() - stringDatePostgresToDate(billB.updatedAt).getTime();
                        }
                        else{
                            return 0;
                        }
                    });
                }
                setBills(tempBills);
                if (onBillUpdated) {
                    onBillUpdated(response.bill);
                }
            }
        }
        catch(error:any){
            handleError(error);
        }
        finally{
            setLoading(false);
        }
    }

    async function onChangeDocumentType(uuidBill:string, type: DocumentType) {
        try{
            console.log("onChangeDocumentType", uuidBill, type);
            setLoading(true);
            const response = await updateDocumentType(investigation.uuid, uuidBill, type);
            setShowSnackbar({ message: "hospital.billing.bill.success.changed", show: true, severity: "success" });    
            if(response.status === 200 && response.bill.uuid){
                const tempBills = [...bills];
                tempBills.push(response.bill);
                setBills(tempBills);
                history.push(HOSPITAL_BILLING_VIEW_DOCUMENT.replace(":uuidDocument", response.bill.uuid));
            }
            setLoading(false);
        }
        catch(error:any){
            handleError(error);
        }

    }
    function onPatientSelected(uuid:string){
        history.push(`/billing/patient/${uuid}`);
    }
    function onCancelBill() {
        if(location.pathname !== HOSPITAL_BILLING){
            history.push(HOSPITAL_BILLING);
        }
        
    }
    const refreshBills = useCallback(async () => {
        if (!investigation) return;
        // SHER-474: the DEFAULT browse owns its data via useBills — refetch the current
        // page instead of bulk-downloading every document.
        if (isDefaultBrowse) {
            browseServer.refetch();
            return;
        }
        setLoading(true);
        let response;
        if(!uuidPatient){
            response = await getDocumentsService(investigation.uuid);
        }
        else{
            response = await getBillsPatientService(investigation.uuid, uuidPatient);
        }

        if (response.status === 200) {
            setBills(response.bills);
        }
        setLoading(false);
    }, [investigation, uuidPatient, isDefaultBrowse, browseServer.refetch]);

    useEffect(() => {
        if (investigation) {
            refreshBills();
        }
    }, [investigation, refreshBills]);

    if (investigation && patients) {
        const surveyAdditionalInfo:ISurvey | undefined = investigation.surveys.find((survey: any) => survey.type === TYPE_ADDITIONAL_INFO_SURVEY);
        return <BillingLocalized key={uuidDocument} withDiscount={hasDiscounts}
                    uuidInvestigation={investigation.uuid as string} hospitalName={investigation.name}
                    personalFields={investigation.personalFields}
                    canCreateInsuranceClaims={canCreateInsuranceClaims}
                    billingInfo={investigation.billingInfo} uuidDocument={uuidDocument}
                    section={action} surveyAdditionalInfo={surveyAdditionalInfo}
                    bills={bills} loading={loading} uuidPatient={uuidPatient} showSnackbar={showSnackbar}
                    canCreateBills={canCreateBills}
                    canCancelDocument={canCancelDocument}
                    onCreateOrUpdateBill={onCreateOrUpdateBill}
                    refreshBills={refreshBills}
                    onChangeDocumentType={onChangeDocumentType}
                    onPatientSelected={(uuid:string) => onPatientSelected(uuid)}
                    navigateToHomeBilling={navigateToHomeBilling}
                    createBill={() => history.push(uuidPatient
                        ? `${HOSPITAL_BILLING_CREATE_BILL}?patient=${uuidPatient}`
                        : HOSPITAL_BILLING_CREATE_BILL)}
                    onCancelBill={onCancelBill}
                    patients={patients}
                    canUpdateInsurance={canUpdateInsurance}
                    canEditPharmacyPrices={canEditPharmacyPrices}
                    browse={isDefaultBrowse ? {
                        bills: browseServer.bills,
                        total: browseServer.total,
                        loading: browseServer.loading,
                        error: browseServer.error,
                        page: browsePage,
                        rowsPerPage: browseRowsPerPage,
                        onPageChange: onBrowsePageChange,
                        onFilterChange: onBrowseFilterChange,
                    } : undefined}
        />
    }
    else {
        return <Loader />;
    }
}

const mapStateToProps = (state: any) => {
    return {
        investigations: state.investigations,
        patients : state.investigations.currentInvestigation && state.patients.data ? state.patients.data[state.investigations.currentInvestigation.uuid] : null
    }
}

export default connect(mapStateToProps, null)(BillingRedux);


interface Props extends LocalizeContextProps {
    patients: IPatient[],
    personalFields: [],
    uuidInvestigation: string,
    hospitalName: string,
    billingInfo: BillingInfo,
    uuidPatient?:string,
    showSnackbar:SnackbarType,
    bills: Bill[];
    canCreateBills: boolean,
    canCancelDocument: boolean,
    canCreateInsuranceClaims: boolean,
    loading: boolean,
    surveyAdditionalInfo?: ISurvey,
    section: BillActions,
    uuidDocument?: string,
    withDiscount: boolean,
    onChangeDocumentType: (uuidBill:string, type: DocumentType) => void,
    onPatientSelected: (uuid:string) => void,
    onCreateOrUpdateBill: (bill: Bill, typeUpdate: TypeBillItemUpdate, paymentRecord?: CreatePaymentRequest, onBillUpdated?: (bill: Bill) => void) => void,
    refreshBills: () => void,
    createBill: () => void,
    onCancelBill: () => void,
    navigateToHomeBilling: () => void,
    canUpdateInsurance: boolean,
    canEditPharmacyPrices: boolean,
    // SHER-474: present only for the DEFAULT browse — drives server-side pagination of the
    // all-patients bill list. Absent for every other section (which use props.bills).
    browse?: {
        bills: any[];
        total: number;
        loading: boolean;
        error: string | null;
        page: number;
        rowsPerPage: number;
        onPageChange: (page: number, rowsPerPage: number) => void;
        onFilterChange: (filters: { documentStatusFilter?: number[]; typeFilter?: number[]; selectedInsurance?: string; startDate?: string; endDate?: string }) => void;
    },
}

export enum BillActions {
    UPDATE = "update",
    PREVIEW = "preview",
    CREATE = "create",
    PATIENT_BILLS = "patient_bills",
    VIEW = "view",
    INSURANCE_BILLS = "insurance_bills",
    VIEW_INSURANCE_BILL = "view_insurance_bill",
    ACCOUNTABILITY = "accountability",
    CANCEL = "cancel",
    DEFAULT = ""
}


const Billing: React.FC<Props> = (props) => {
    const { patients, loadingPatients } = usePatients(props.uuidInvestigation);
    const [showSnackbar, setShowSnackbar] = useSnackBarState();
    const [showModal, setShowModal] = useState(false);
    const [actionBill, setActionBill] = useState<BillActions>(props.section);
    const [currentBill, setCurrentBill] = useState<Bill | null>(null);
    const [loadingCurrentBill, setLoadingCurrentBill] = useState(false);
    const [uuidBillPreview, setUuidBillPreview] = useState<string | null>(null);
    // SHER-423: cancel/annul confirmation flow.
    const [cancelTargetUuid, setCancelTargetUuid] = useState<string | null>(null);
    const [cancelling, setCancelling] = useState(false);
    const { paymentRecords, pec } = useBillPaymentData(props.uuidInvestigation, currentBill?.uuid, currentBill?.totalGross);
    const insurances = useSelector((state:ReduxStore) => 
        state.investigations.currentInvestigation?.insurances
    );
    
    // Fetch organizations for insurance bills
    const [organizations, organizationsLoading, organizationsError] = useOrganizations(props.uuidInvestigation);
    
    // TODO: Insurance report functionality may be removed entirely
    // Removed: showInsuranceReportModal state and related functionality
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const { config: pdfConfig } = usePDFConfig();
    const { departments, researchers } = useDepartments();
    const theme = useTheme() as any; // Cast to access custom theme properties
    
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const editing = location.pathname === HOSPITAL_BILLING_EDIT;
    const uuidBill = useParams<{uuidBill?:string}>().uuidBill;

    useEffect(() => {
        if (location.pathname === HOSPITAL_BILLING && !props.billingInfo) {
            history.push(HOSPITAL_BILLING_EDIT);
        }
    }, []);

    // Note: Organizations are now fetched via useOrganizations hook above

    useEffect(() => {
        if ([BillActions.VIEW, BillActions.PREVIEW].includes(actionBill) && (props.uuidDocument || uuidBillPreview)) {
            setLoadingCurrentBill(true);
            const uuid = props.uuidDocument || uuidBillPreview;
            if(uuid){
            getBillByUuidService(props.uuidInvestigation, uuid)
                .then(res => {
                    setCurrentBill(res.bill); // Adjust if your API returns differently
                        setLoadingCurrentBill(false);
                    })
                    .catch(() => setLoadingCurrentBill(false));
            }
        } else {
            setCurrentBill(null);
        }
    }, [actionBill, props.uuidDocument, props.uuidInvestigation,  uuidBillPreview]);

    async function resetSnackBar() {
        setShowSnackbar({ show: false });
        if(actionBill === BillActions.CREATE){
            // SHER-451: after creating a bill, open the new bill's detail page directly
            // instead of bouncing back to the global /billing list, which reloads every
            // hospital invoice and forces the cashier to find the bill again before
            // taking payment. Fall back to the list if the new uuid isn't available.
            if(currentBill && currentBill.uuid){
                history.push(HOSPITAL_BILLING_VIEW_DOCUMENT.replace(":uuidDocument", currentBill.uuid));
            }
            else{
                props.navigateToHomeBilling();
            }
        }
    }
    async function onCancelBill() {
        setShowModal(false);
        setCurrentBill(null);
        await dispatch(resetBillItems());
        props.onCancelBill();
    }
    
    function toogleEditBillingInfo() {
        if(editing){
            history.push(HOSPITAL_BILLING);
        }
        else{
            history.push(HOSPITAL_BILLING_EDIT);
        }
    }

    useEffect(() => {
        if (props.showSnackbar) {
            setShowSnackbar(props.showSnackbar);
        }
    }, [props.showSnackbar]);


    function makeActionBill(uuidBill: string, action: BillActions) {
        console.log(uuidBill);
        // SHER-423: cancel opens its own confirmation dialog, not the bill modal.
        if (action === BillActions.CANCEL) {
            setCancelTargetUuid(uuidBill);
            return;
        }
        // SHER-474: in the server-paginated browse, props.bills is empty (the page lives in
        // props.browse.bills), so resolve the clicked bill from whichever list is active.
        const tempBill = (props.browse ? props.browse.bills : props.bills).find((bill) => bill.uuid === uuidBill);

        if (tempBill) {
            
            const patient = patients.find((patient) => patient.uuid === tempBill.uuidPatient);
            if (patient) {
                setActionBill(action);
                setShowModal(true);
                setUuidBillPreview(uuidBill);
                setCurrentBill(null);
            }
        }

    }

    async function onCloseModal() {
        setShowModal(false);
        setCurrentBill(null);
        setActionBill(BillActions.DEFAULT);
    }

    // SHER-423: confirm cancellation of an unpaid document. The API re-validates
    // eligibility and returns DOCUMENT_HAS_PAYMENT if a payment exists.
    async function handleConfirmCancel() {
        if (!cancelTargetUuid) return;
        setCancelling(true);
        try {
            await cancelBillService(props.uuidInvestigation, cancelTargetUuid, null);
            setCancelTargetUuid(null);
            setShowSnackbar({ message: "hospital.billing.bill.cancel.success", show: true, severity: "success" });
            props.refreshBills();
        } catch (error: any) {
            const code = error?.errorMessage;
            const message = code === "DOCUMENT_HAS_PAYMENT"
                ? "hospital.billing.bill.cancel.error.has_payment"
                : "hospital.billing.bill.cancel.error.generic";
            setShowSnackbar({ message, show: true, severity: "error" });
        } finally {
            setCancelling(false);
        }
    }

    function onBillingInfoSuccesfullyUpdated(type: BillItemModes) {
        if (type === BillItemModes.SHOW) {
            setShowSnackbar({ message: "hospital.billing.billing_info.success.updated", show: true, severity: "success" });
        }
        else {
            setShowSnackbar({ message: "hospital.billing.billables.success.updated", show: true, severity: "success" });
        }
    }

    function handleBillUpdated(updatedBill: Bill) {
        setCurrentBill(updatedBill);
    }

    // TODO: Insurance report functionality may be removed entirely
    // Removed: handleGenerateInsurancePDF function

    function renderCore() {
        if (props.loading || loadingPatients) {
            return <Loader />;
        }
        else if (editing) {
            return <EditBilling uuidInvestigation={props.uuidInvestigation} 
                        billables={[]} 
                        withDiscount={props.withDiscount}
                        billingInfo={props.billingInfo} 
                        onBillingInfoSuccesfullyUpdated={(type: BillItemModes) => onBillingInfoSuccesfullyUpdated(type)} />
        }
        else if(props.section === BillActions.PATIENT_BILLS && props.uuidPatient){
            const currentPatient = patients.find((patient) => patient.uuid === props.uuidPatient);
            return(<>
                        { renderBillForm() }
                        <BillsPatient patient={currentPatient} uuidPatient={props.uuidPatient} bills={props.bills}
                            canCreateBugdet={Boolean(props.billingInfo.params.budgets)}
                            canCancelDocument={props.canCancelDocument}
                            currency={props.billingInfo.currency} languageCode={props.activeLanguage.code}
                            hasBudgets={Boolean(props.billingInfo.params.budgets)}
                            insurances={insurances}
                            makeActionBillCallBack={makeActionBill}/>
            </>)
        }
        else if(props.section === BillActions.VIEW && props.uuidDocument && currentBill){
            const currentPatient = patients.find((patient) => patient.uuid === currentBill.uuidPatient);
            if (loadingCurrentBill) return <Loader />;
            return (<>
                        { renderBillForm() }
                        <BillView bill={currentBill} billStatus={currentBill.status} billType={currentBill.type} 
                            billingInfo={props.billingInfo} hospitalName={props.hospitalName} 
                            canCreateInsuranceClaims={props.canCreateInsuranceClaims}
                            canUpdateInsurance={props.canUpdateInsurance}
                            patient={currentPatient!} hasBudgets={Boolean(props.billingInfo.params.budgets)} languageCode={props.activeLanguage.code} canUpdateBill={isBillEditable(currentBill.status)} 
                            currency={props.billingInfo.currency} uuidInvestigation={props.uuidInvestigation} idBillingInfo={props.billingInfo.id}
                            print={false} withDiscount={props.withDiscount} surveyAdditionalInfo={props.surveyAdditionalInfo}
                            onUpdateBill={(bill: Bill, typeUpdate:TypeBillItemUpdate, paymentRecord?: CreatePaymentRequest) => props.onCreateOrUpdateBill(bill, typeUpdate, paymentRecord, handleBillUpdated)}
                            otherDraftBills={props.bills
                                .filter((b) => b.uuid && b.uuid !== currentBill.uuid && b.uuidPatient === currentBill.uuidPatient && b.status === DocumentStatus.DRAFT)
                                .map((b) => ({ uuid: b.uuid as string, documentNumber: b.documentNumber }))}
                            onSplitDone={(sourceBill: Bill) => { handleBillUpdated(sourceBill); props.refreshBills(); }}
                            onChangeDocumentType={props.onChangeDocumentType}
                            onCancelBill={onCancelBill}
                            canEditPharmacyPrices={props.canEditPharmacyPrices}
                        />
                    </>)
        }
        else if(props.section === BillActions.INSURANCE_BILLS){
            if (!props.billingInfo) {
                return <Loader />;
            }
            return (
                <InsuranceBillsTable
                    investigationUuid={props.uuidInvestigation}
                    organizations={organizations}
                    languageCode={props.activeLanguage.code}
                    currency={props.billingInfo.currency}
                    fetchPatientFn={fetchPatient}
                    onBillClick={(billUuid: string) => {
                        history.push(HOSPITAL_BILLING_INSURANCE_VIEW.replace(':uuidBill', billUuid));
                    }}
                />
            );
        }
        else if(props.section === BillActions.VIEW_INSURANCE_BILL && uuidBill){
            if (!props.billingInfo || !uuidBill) {
                return <Loader />;
            }
            return (
                <InsuranceBillView
                    investigationUuid={props.uuidInvestigation}
                    billUuid={uuidBill}
                    currency={props.billingInfo.currency}
                    languageCode={props.activeLanguage.code}
                    billingInfo={props.billingInfo}
                    fetchPatientFn={fetchPatient}
                    onClose={() => {
                        history.push(HOSPITAL_BILLING_INSURANCE);
                    }}
                    onBillUpdated={() => {
                        // Optionally refresh the list or show a success message
                    }}
                />
            );
        }
        else if(props.section === BillActions.ACCOUNTABILITY){
            if (!props.billingInfo) {
                return <Loader />;
            }
            return (
                <AccountabilityTable
                    investigationUuid={props.uuidInvestigation}
                    languageCode={props.activeLanguage.code}
                    currency={props.billingInfo.currency}
                />
            );
        }
        else {
            if (props.billingInfo) {
                if(actionBill === BillActions.CREATE){
                    return <BillingCreateLocalized patients={patients} personalFields={props.personalFields} currency={props.billingInfo.currency} 
                                uuidInvestigation={props.uuidInvestigation} canCreateBugdet={Boolean(props.billingInfo.params.budgets)}
                                idBillingInfo={props.billingInfo.id} languageCode={props.activeLanguage.code} withDiscount={props.withDiscount} 
                                onCreateBill={(bill: Bill) => props.onCreateOrUpdateBill(bill, TypeBillItemUpdate.BillItems, undefined, handleBillUpdated)}
                                onCancelBill={onCancelBill} 
                                />
                }
                return (
                    <>
                        { renderBillForm() }
                        {/* SHER-474/475: in the server-paginated browse, find-a-patient is the way to
                            see one patient's bills (name search can't be a server filter — names are
                            encrypted). Reuses FindPatient → /billing/patient/:uuid. */}
                        {props.browse && (
                            <FindPatient personalFields={props.personalFields} codeLanguage={props.activeLanguage.code}
                                uuidInvestigation={props.uuidInvestigation}
                                onPatientSelected={(id) => props.onPatientSelected(String(id))} />
                        )}
                        <BillsTable hasBudgets={Boolean(props.billingInfo.params.budgets)} patients={patients} currency={props.billingInfo.currency}
                            insurances={insurances} languageCode={props.activeLanguage.code}
                            bills={props.browse ? props.browse.bills : props.bills} canCreateBugdet={Boolean(props.billingInfo.params.budgets)}
                            canCancelDocument={props.canCancelDocument}
                            makeActionBillCallBack={makeActionBill}
                            // SHER-474: DEFAULT browse → server-driven pagination + multi-value filters.
                            serverPagination={Boolean(props.browse)}
                            total={props.browse?.total}
                            loading={props.browse?.loading}
                            error={props.browse?.error}
                            page={props.browse?.page}
                            rowsPerPage={props.browse?.rowsPerPage}
                            onServerPageChange={props.browse?.onPageChange}
                            onServerFilterChange={props.browse?.onFilterChange}/>
                    </>
                );
            }
        }
    }
    async function handleDownloadBillPDF() {
        if (!currentBill || !pdfConfig) {
            return;
        }

        try {
            setGeneratingPDF(true);
            const currentPatient = patients.find((patient) => patient.uuid === currentBill.uuidPatient);
            if (!currentPatient) {
                throw new Error('Patient not found');
            }

            // Get prescribing doctor
            const prescribingDoctor = currentBill.uuidPrescribingDoctor 
                ? researchers.find((r) => r.uuid === currentBill.uuidPrescribingDoctor) 
                : undefined;

            // Get department
            const department = currentBill.uuidDepartment 
                ? departments.find((d) => d.uuid === currentBill.uuidDepartment) 
                : undefined;

            // Patient insurance will be fetched by the component if needed
            // For now, we pass null - the component handles this gracefully
            const patientInsurance = null;

            // Convert InsuranceClaim to the format expected by BillPDFDocument
            const pecForPDF = pec ? {
                organizationName: pec.organizationName || null,
                requestedAmount: pec.requestedAmount || null,
                interventionDate: pec.interventionDate ? (pec.interventionDate instanceof Date ? pec.interventionDate.toISOString() : String(pec.interventionDate)) : null,
                guaranteeLetterIssueDate: pec.guaranteeLetterIssueDate ? (pec.guaranteeLetterIssueDate instanceof Date ? pec.guaranteeLetterIssueDate.toISOString() : String(pec.guaranteeLetterIssueDate)) : null,
                guaranteeLetterNumber: pec.guaranteeLetterNumber || null,
                amountInLetters: pec.amountInLetters || null,
            } : {
                organizationName: null,
                requestedAmount: null,
                interventionDate: null,
                guaranteeLetterIssueDate: null,
                guaranteeLetterNumber: null,
                amountInLetters: null,
            };

            // Create PDF document
            const pdfDocument = (
                <BillPDFDocument
                    bill={currentBill}
                    type={currentBill.type}
                    patient={currentPatient}
                    uuidInvestigation={props.uuidInvestigation}
                    uuidPrescribingDoctor={currentBill.uuidPrescribingDoctor}
                    uuidDepartment={currentBill.uuidDepartment}
                    currency={props.billingInfo.currency}
                    locale={props.activeLanguage.code}
                    config={pdfConfig}
                    paymentRecords={paymentRecords}
                    pec={pecForPDF}
                    prescribingDoctor={prescribingDoctor}
                    department={department}
                    patientInsurance={patientInsurance}
                />
            );

            // Generate localized filename
            const filename = generateBillPDFFilename(currentBill, props.activeLanguage.code);

            // Download PDF
            await downloadPDF(pdfDocument, filename);
            setShowModal(false);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setGeneratingPDF(false);
        }
    }

    function renderBillForm() {
        switch (actionBill) {
            case BillActions.PREVIEW:
                let contentModal = null;
                if(loadingCurrentBill || !currentBill){
                    contentModal = <Loader />;
                }
                else if (!pdfConfig) {
                    contentModal = <Loader />;
                }
                else{
                    const currentPatient = patients.find((patient) => patient.uuid === currentBill!.uuidPatient);
                    
                    contentModal = (
                        <Grid container spacing={2} justifyContent="center" style={{ padding: '16px' }}>
                            <Grid item xs={12} style={{ textAlign: 'center' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={handleDownloadBillPDF}
                                    disabled={generatingPDF}
                                    fullWidth
                                    sx={{
                                        backgroundColor: generatingPDF 
                                            ? 'gray' 
                                            : theme.buttonContinue.primary.background,
                                        color: theme.buttonContinue.primary.color,
                                        '&:hover': {
                                            backgroundColor: generatingPDF 
                                                ? 'gray' 
                                                : theme.buttonContinue.primary.background,
                                            opacity: 0.9,
                                        },
                                    }}
                                >
                                    <Translate id="hospital.billing.bill.download">Download PDF</Translate>
                                </Button>
                            </Grid>
                        </Grid>
                    );
                }
                
                return (
                    <Modal key="modal" maxWidth="xs" open={showModal} title={!currentBill ? "Create bill" : ""} closeModal={() => onCloseModal()}>
                        {contentModal}
                    </Modal>
                )
            default:
                return null;
        }
    }

    return (
        <>
            <Helmet title="Billing Dashboard" />
            <Snackbar
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                open={showSnackbar.show}
                autoHideDuration={2000}
                onClose={resetSnackBar}>
                <div>
                    {
                        (showSnackbar.message && showSnackbar.severity) &&
                        <Alert onClose={() => setShowSnackbar({ show: false })} severity={showSnackbar.severity}>
                            <Translate id={showSnackbar.message} />
                        </Alert>
                    }
                </div>
            </Snackbar>

            {/* SHER-423: cancel/annul confirmation dialog */}
            <Modal key="cancel-bill-modal" maxWidth="xs" open={Boolean(cancelTargetUuid)}
                title={<Translate id="hospital.billing.bill.cancel.title" />}
                closeModal={() => { if (!cancelling) setCancelTargetUuid(null); }}>
                <Grid container spacing={2} style={{ padding: '8px' }}>
                    <Grid item xs={12}>
                        <Typography variant="body1">
                            <Translate id="hospital.billing.bill.cancel.confirm_message" />
                        </Typography>
                    </Grid>
                    <Grid item xs={12} container justifyContent="flex-end" spacing={1}>
                        <Grid item>
                            <Button variant="outlined" disabled={cancelling} onClick={() => setCancelTargetUuid(null)}>
                                <Translate id="general.cancel" />
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button variant="contained" color="error" disabled={cancelling} onClick={handleConfirmCancel}>
                                <Translate id="hospital.billing.bill.cancel.confirm_button" />
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Modal>

            <Grid justifyContent="space-between" direction='row' container padding={2} style={{ color: "white" }}>
                <Grid item xs={12} container>
                    <Grid item xs={12} style={{ paddingBottom: '1rem' }}>
                        <SectionHeader section="billing" edit={editing} 
                            editCallback={toogleEditBillingInfo}  />
                    </Grid>
                    {!props.billingInfo ? (
                        <Grid item xs={12}>
                            <TypographyStyled variant="body2" gutterBottom >
                                <Translate id="hospital.billing.no_billing_info" />
                            </TypographyStyled>
                        </Grid>
                    ) : (
                        <>
                            {/* Tabs for switching between Patient Bills, Insurance Bills, and Accountability */}
                            {!editing && (props.section === BillActions.DEFAULT || props.section === BillActions.INSURANCE_BILLS || props.section === BillActions.VIEW_INSURANCE_BILL || props.section === BillActions.ACCOUNTABILITY) && (
                                <Grid item xs={12} style={{ marginBottom: '1rem' }}>
                                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                                        <Tabs 
                                            value={
                                                props.section === BillActions.INSURANCE_BILLS || props.section === BillActions.VIEW_INSURANCE_BILL ? 1 : 
                                                props.section === BillActions.ACCOUNTABILITY ? 2 : 
                                                0
                                            }
                                            onChange={(event, newValue) => {
                                                if (newValue === 0) {
                                                    history.push(HOSPITAL_BILLING);
                                                } else if (newValue === 1) {
                                                    history.push(HOSPITAL_BILLING_INSURANCE);
                                                } else if (newValue === 2) {
                                                    history.push(HOSPITAL_BILLING_ACCOUNTABILITY);
                                                }
                                            }}
                                            sx={{
                                                '& .MuiTab-root': {
                                                    color: theme.palette.text.secondary, // Automatically adapts to background
                                                    fontWeight: theme.typography.fontWeightRegular,
                                                    textTransform: 'none',
                                                    fontSize: '0.95rem',
                                                    '&.Mui-selected': {
                                                        color: theme.palette.primary.main || '#1976d2',
                                                        fontWeight: theme.typography.fontWeightMedium,
                                                    },
                                                    '&:hover': {
                                                        color: theme.palette.text.primary, // Brighter on hover
                                                    },
                                                },
                                                '& .MuiTabs-indicator': {
                                                    backgroundColor: theme.palette.primary.main || '#1976d2',
                                                },
                                            }}
                                        >
                                            <Tab 
                                                label={<Translate id="hospital.billing.patient_bills" />} 
                                                value={0}
                                            />
                                            <Tab 
                                                label={<Translate id="hospital.billing.insurance_bills.title" />} 
                                                value={1}
                                                onClick={() => {
                                                    // Always navigate to insurance bills list when clicking this tab
                                                    if (location.pathname !== HOSPITAL_BILLING_INSURANCE) {
                                                        history.push(HOSPITAL_BILLING_INSURANCE);
                                                    }
                                                }}
                                            />
                                            <Tab 
                                                label={<Translate id="hospital.billing.accountability.title" />} 
                                                value={2}
                                            />
                                        </Tabs>
                                    </Box>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Grid container spacing={2} alignItems="center">
                                    {props.canCreateBills && (props.section === BillActions.DEFAULT || props.section === BillActions.PATIENT_BILLS) && (
                                        <Grid item>
                                            <ButtonAdd 
                                                disabled={props.section === BillActions.CREATE || props.loading || editing}
                                                type="button" 
                                                data-testid="add_bill"
                                                onClick={props.createBill} 
                                            />
                                        </Grid>
                                    )}
                                    {/* TODO: Insurance report functionality may be removed entirely - Button removed */}
                                </Grid>
                            </Grid>
                        </>
                    )}
                </Grid>

            </Grid>
            {
                renderCore()
            }

            {/* TODO: Insurance report functionality may be removed entirely - Modal removed */}

        </>
    );
}

export const BillingLocalized = withLocalize(Billing);
const BillingCreateLocalized = withLocalize(BillCreate);